"use client";
import React, { useState, useEffect } from "react";
import { useQueryClient, useQuery, useMutation } from "react-query";
import { useParams } from "react-router-dom";
import { QK } from "../../../base/qk";
import { useSnackbar } from "notistack";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  IconButton,
  Divider,
  alpha,
  useTheme,
  FormControlLabel,
  Switch,
  CircularProgress,
  Stack,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SaveIcon from "@mui/icons-material/Save";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import ReceiptIcon from "@mui/icons-material/Receipt";
import CustomSelect from "../../../components/FormFields/CustomSelect/CustomSelect";
import CustomInput from "../../../components/FormFields/CustomInput/CustomInput";
import { FunderOneTypes, SplitFunderOneTypes } from "./Funding.config";
import { createFundingProfile, updateFundingProfile } from "../../../api/services/Client";
import { getFundersLookup } from "../../../api/services/Billing/funder";
import { getFunderRates } from "../../../api/services/Billing/funderRates";

const MANUAL_SERVICES = [
  { label: "Personal Care", value: "personal_care" },
  { label: "Sleep-In", value: "sleep_in" },
  { label: "Waking Night", value: "waking_night" },
  { label: "Domestic Support", value: "domestic_support" },
  { label: "Live-in Care", value: "live_in" },
  { label: "Social Support", value: "social_support" },
];

const MANUAL_BILLING_TYPES = [
  { label: "Full Funder", value: "Full Funder" },
  { label: "Split Funder", value: "Split Funder" },
  { label: "Private Pay", value: "Private Pay" },
];

const FundingModal = ({ show, onHide, clientID: clientIDProp, editData }) => {
  const theme = useTheme();
  const queryClient = useQueryClient();
  const { id: clientIDFromParams } = useParams();
  const clientID = clientIDProp || clientIDFromParams;
  const { enqueueSnackbar } = useSnackbar();

  const [selectedService, setSelectedService] = useState("");
  const [selectedBillingType, setSelectedBillingType] = useState("");
  const [selectedFunder1Type, setSelectedFunder1Type] = useState("");

  const [formData, setFormData] = useState({
    funder_1: "",
    funder_2: "",
    client_weekly_contribution: "",
    council_max_weekly_allowance: "",
    invoice_mileage: false,
    funder_rate: "",
  });

  const isFullFunder = selectedBillingType === "Full Funder";
  const isSplitFunder = selectedBillingType === "Split Funder";
  const isFunderNeeded = isFullFunder || isSplitFunder;

  useEffect(() => {
    if (show) {
      if (editData) {
        setSelectedService(editData.service || "");
        setSelectedBillingType(editData.billing_type || "");
        setSelectedFunder1Type(editData.funder_1_type || "");

        setFormData({
          funder_1: editData.funder_1 || "",
          funder_2: editData.funder_2 || "",
          client_weekly_contribution: editData.client_weekly_contribution || "",
          council_max_weekly_allowance: editData.council_max_weekly_allowance || "",
          invoice_mileage: !!editData.invoice_mileage,
          funder_rate: editData.funder_rate || "",
        });
      } else {
        setSelectedService("");
        setSelectedBillingType("");
        setSelectedFunder1Type("");
        setFormData({
          funder_1: "",
          funder_2: "",
          client_weekly_contribution: "",
          council_max_weekly_allowance: "",
          invoice_mileage: false,
          funder_rate: "",
        });
      }
    }
  }, [show, editData]);

  const { data: fundersLookup = [] } = useQuery(
    [QK.funders, "lookup", selectedBillingType],
    () => getFundersLookup(selectedBillingType === "Full Funder" ? "COUNCIL" : ""),
    { enabled: show && isFunderNeeded }
  );

  const { data: rateCards = [] } = useQuery(
    [QK.rateCards, formData.funder_1],
    () => getFunderRates(formData.funder_1),
    { enabled: !!formData.funder_1 }
  );

  const funderOptions = fundersLookup?.map((f) => ({
    value: f.id,
    label: f.name,
  })) || [];

  const rateOptions = rateCards?.map((rc) => ({
    value: rc.id,
    label: `${rc.service_name} (${rc.service_code})`,
  })) || [];

  const serviceOptions = MANUAL_SERVICES.map((s) => ({
    value: s.value,
    label: s.label,
  }));

  const billingOptions = MANUAL_BILLING_TYPES.map((b) => ({
    value: b.value,
    label: b.label,
  }));

  const F1options = (isSplitFunder ? SplitFunderOneTypes : FunderOneTypes).map((opt) => ({
    value: opt.value,
    label: opt.label,
  }));

  const handleChange = (e) => {
    const { name, value, checked, type } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleBillingTypeChange = (e) => {
    const val = e.target.value;
    setSelectedBillingType(val);
    setSelectedFunder1Type(val === "Full Funder" ? "Funder" : "");
    setFormData((prev) => ({
      ...prev,
      funder_1: "",
      funder_2: "",
      funder_rate: "",
    }));
  };

  const mutation = useMutation(
    (payload) =>
      editData ? updateFundingProfile(editData.id, payload) : createFundingProfile(payload),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([QK.fundingProfiles, clientID]);
        queryClient.invalidateQueries([QK.clientPaymentServices, clientID]);
        enqueueSnackbar(`Profile saved successfully.`, { variant: "success" });
        onHide();
      },
      onError: (error) => {
        enqueueSnackbar(error?.response?.data?.message || "Failed to save profile", {
          variant: "error",
        });
      },
    }
  );

  const handleSave = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    if (!selectedService) {
      enqueueSnackbar("Please select a service type", { variant: "warning" });
      return;
    }

    if (!selectedBillingType) {
      enqueueSnackbar("Please select a billing category", { variant: "warning" });
      return;
    }

    const payload = {
      client: clientID,
      service: selectedService,
      billing_type: selectedBillingType,
      funder_1_type: isFullFunder ? "Funder" : selectedFunder1Type,
      funder_1: formData.funder_1 || null,
      funder_2: formData.funder_2 || null,
      client_weekly_contribution: formData.client_weekly_contribution || 0,
      council_max_weekly_allowance: formData.council_max_weekly_allowance || null,
      funder_rate: formData.funder_rate || null,
      invoice_mileage: formData.invoice_mileage,
      is_active: true,
    };
    mutation.mutate(payload);
  };

  return (
    <Dialog
      open={show}
      onClose={onHide}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "16px",
          boxShadow: "0px 24px 48px -12px rgba(16, 24, 40, 0.25)",
          maxHeight: "90vh",
        },
      }}
    >
      <DialogTitle sx={{ p: 0 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            p: 2.5,
            pb: 1.5,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: "10px",
                bgcolor: alpha(theme.palette.primary.main, 0.08),
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <AttachMoneyIcon sx={{ color: theme.palette.primary.main, fontSize: 24 }} />
            </Box>
            <Box>
              <Typography variant="h6" fontWeight={700} color="#1A1A2E">
                {editData ? "Update Funding Profile" : "New Funding Profile"}
              </Typography>
              <Typography variant="caption" color="#6B7280">
                {editData ? "Edit funding details" : "Create a new funding profile"}
              </Typography>
            </Box>
          </Box>
          <IconButton
            onClick={onHide}
            size="small"
            sx={{
              color: "#9CA3AF",
              "&:hover": { bgcolor: "#F3F4F6", color: "#4B5563" },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
        <Divider sx={{ opacity: 0.4 }} />
      </DialogTitle>

      <DialogContent sx={{ p: 2.5, pt: 2 }}>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2.5 }}>
          <Box sx={{ flex: "1 1 45%", minWidth: "240px" }}>
            <CustomSelect
              options={serviceOptions}
              label="Service Type"
              name="service"
              value={selectedService}
              onChange={(e) => setSelectedService(e.target.value)}
              required
              fullWidth
              placeholder="Select service type"
            />
          </Box>

          <Box sx={{ flex: "1 1 45%", minWidth: "240px" }}>
            <CustomSelect
              options={billingOptions}
              label="Billing Category"
              name="billing_type"
              value={selectedBillingType}
              onChange={handleBillingTypeChange}
              required
              fullWidth
              placeholder="Select billing category"
            />
          </Box>

          {isFunderNeeded && (
            <>
              {isSplitFunder && (
                <Box sx={{ flex: "1 1 45%", minWidth: "240px" }}>
                  <CustomSelect
                    options={F1options}
                    label="Primary Payer Type (F1)"
                    name="funder_1_type"
                    value={selectedFunder1Type}
                    onChange={(e) => setSelectedFunder1Type(e.target.value)}
                    required
                    fullWidth
                    placeholder="Select payer type"
                  />
                </Box>
              )}

              {(isFullFunder || selectedFunder1Type === "Funder") && (
                <Box sx={{ flex: "1 1 45%", minWidth: "240px" }}>
                  <CustomSelect
                    options={funderOptions}
                    label={isFullFunder ? "Select Council" : "Select Funder 1"}
                    name="funder_1"
                    value={formData.funder_1 || ""}
                    onChange={handleChange}
                    required
                    fullWidth
                    placeholder={isFullFunder ? "Select council..." : "Select funder..."}
                  />
                </Box>
              )}
            </>
          )}

          {isSplitFunder && (
            <>
              <Box sx={{ flex: "1 1 45%", minWidth: "240px" }}>
                <CustomInput
                  label="Service User Contribution (£)"
                  name="client_weekly_contribution"
                  type="number"
                  value={formData.client_weekly_contribution || ""}
                  onChange={handleChange}
                  fullWidth
                  placeholder="0.00"
                />
              </Box>
              <Box sx={{ flex: "1 1 45%", minWidth: "240px" }}>
                <CustomSelect
                  options={funderOptions}
                  label="Select Council (F2)"
                  name="funder_2"
                  value={formData.funder_2 || ""}
                  onChange={handleChange}
                  required
                  fullWidth
                  placeholder="Select council..."
                />
              </Box>
            </>
          )}

          <Box sx={{ flex: "1 1 45%", minWidth: "240px" }}>
            <CustomSelect
              options={rateOptions}
              label="Price Rate Card"
              name="funder_rate"
              value={formData.funder_rate || ""}
              onChange={handleChange}
              disabled={!formData.funder_1}
              required={isFunderNeeded}
              fullWidth
              placeholder={formData.funder_1 ? "Select rate card..." : "Select funder first"}
            />
          </Box>

          <Box sx={{ width: "100%" }}>
            <Divider sx={{ my: 1 }} />
            <FormControlLabel
              control={
                <Switch
                  name="invoice_mileage"
                  onChange={handleChange}
                  checked={!!formData.invoice_mileage}
                  sx={{
                    "& .MuiSwitch-switchBase.Mui-checked": {
                      color: "#2469F0",
                    },
                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                      backgroundColor: "#2469F0",
                    },
                  }}
                />
              }
              label={
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <ReceiptIcon sx={{ fontSize: 16, color: "#6B7280" }} />
                  <Typography variant="body2" fontWeight={500}>
                    Include Mileage in Invoice
                  </Typography>
                </Box>
              }
            />
          </Box>
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          p: 2.5,
          pt: 0,
          gap: 1.5,
          borderTop: "1px solid",
          borderColor: "#E2E8F0",
        }}
      >
        <Button
          onClick={onHide}
          variant="outlined"
          disabled={mutation.isLoading}
          sx={{
            textTransform: "none",
            fontWeight: 500,
            borderRadius: "10px",
            borderColor: "#D0D5DD",
            color: "#344054",
            px: 3,
            "&:hover": {
              bgcolor: "#F9FAFB",
              borderColor: "#98A2B3",
            },
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={mutation.isLoading}
          startIcon={mutation.isLoading ? <CircularProgress size={20} /> : <SaveIcon />}
          sx={{
            textTransform: "none",
            fontWeight: 600,
            borderRadius: "10px",
            bgcolor: "#2469F0",
            px: 4,
            "&:hover": {
              bgcolor: "#1A5BD4",
              boxShadow: "0px 4px 12px rgba(36, 105, 240, 0.3)",
            },
          }}
        >
          {mutation.isLoading ? "Saving..." : "Save Profile"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FundingModal;